import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { axios, UserService, ValidationService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const SITTER_GIRL_CROWN_BG =
  'https://assets.urbansitter.com/static/img/backgrounds/sitter-girl-crown-lg.webp';

// Match Angular's xxx-xxx-xxxx mask (not parentheses).
const formatPhone = (raw?: string): string => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Mirror Angular AnalyticsService.triggerGTMEvent (sitter-confirm.js:97,122).
const pushGTM = (payload: Record<string, unknown>): void => {
  const dl = (window as unknown as { dataLayer?: unknown[] }).dataLayer;
  if (Array.isArray(dl)) dl.push(payload);
};

export const Confirm = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const sitter = stores.sitter.sitter;

  const [firstName, setFirstName] = useState(sitter?.firstName ?? '');
  const [lastName, setLastName] = useState(sitter?.lastName ?? '');
  const [email, setEmail] = useState((sitter as { email?: string } | undefined)?.email ?? '');
  const [phone, setPhone] = useState(formatPhone(sitter?.phone));
  const [submitting, setSubmitting] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  useEffect(() => {
    document.title = 'UrbanSitter - Confirm Details';
  }, []);

  useEffect(() => {
    if (user && !user.id) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const isAlpha = (s: string): boolean => /^[a-zA-Z][a-zA-Z\s'-]*$/.test(s.trim());
  const isEmailValid = (s: string): boolean => {
    try {
      return typeof (ValidationService as { isEmail?: (v: string) => boolean })?.isEmail === 'function'
        ? (ValidationService as unknown as { isEmail: (v: string) => boolean }).isEmail(s)
        : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    } catch {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    }
  };
  const isPhoneValid = (s: string): boolean => s.replace(/\D/g, '').length === 10;

  const validFirst = firstName.trim().length > 0 && isAlpha(firstName);
  const validLast = lastName.trim().length > 0 && isAlpha(lastName);
  const validEmail = isEmailValid(email);
  const validPhone = isPhoneValid(phone);

  const allValid = validFirst && validLast && validEmail && validPhone;

  const submit = async (): Promise<void> => {
    if (!allValid) {
      setIsInvalid(true);
      return;
    }
    if (submitting || !sitter?.id) return;
    setSubmitting(true);
    try {
      await UserService.patch(sitter.id, {
        id: sitter.id,
        firstName,
        lastName,
        email,
        phone,
      } as never);
      await stores.user.refreshUser?.();

      pushGTM({ event: 'sitter-nux-event', nuxTrigger: 'contact info confirmed', uid: sitter.id });

      const redirectTo = searchParams.get('redirectTo');
      if (redirectTo) {
        const expires = new Date();
        expires.setHours(expires.getHours() + 1);
        // Cookie name must match Angular's storageConstants.REDIRECT_URL ('redirectTo'),
        // which the auth guard reads in apps/angular/.../apps/app.js:1623.
        document.cookie = `redirectTo=${encodeURIComponent(redirectTo)}; expires=${expires.toUTCString()}; path=/`;
      }

      navigate('/sitter/registration/location');
    } catch (e) {
      pushGTM({ event: 'sitter-nux-event', nuxTrigger: 'contact info confirmation error', uid: sitter.id });
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.confirm.submitFailed'));
      }
      setSubmitting(false);
    }
  };

  const errorClass = 'text-sm text-red-600 mt-1';

  return (
    <div className='relative min-h-screen'>
      <div
        aria-hidden='true'
        className='hidden md:block absolute top-0 right-0 h-full w-1/2 lg:w-3/5 bg-no-repeat bg-right-top bg-cover pointer-events-none'
        style={{ backgroundImage: `url(${SITTER_GIRL_CROWN_BG})` }}
      />
      <div className='relative container py-8 max-w-xl md:ml-0 md:mr-auto md:pl-8 md:pr-0'>
      <div className='us-card rounded p-6'>
        <h1 className='text-2xl font-semibold mb-4'>{t('pages.confirm.heading')}</h1>
        <h3 className='text-base font-medium mb-4'>{t('pages.confirm.prompt')}</h3>

        <div className='grid grid-cols-2 gap-3 mb-3'>
          <div>
            <label htmlFor='confirm-first-name' className='block text-sm mb-1'>
              {t('pages.confirm.firstNameLabel')}
            </label>
            <input
              type='text'
              id='confirm-first-name'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.confirm.firstNamePlaceholder')}
              value={firstName}
              onChange={(e): void => {
                setFirstName(e.target.value);
                setTouched((p) => ({ ...p, firstName: true }));
              }}
            />
            {!validFirst && (touched.firstName || isInvalid) && (
              <div className={errorClass}>{t('pages.confirm.firstNameRequired')}</div>
            )}
          </div>
          <div>
            <label htmlFor='confirm-last-name' className='block text-sm mb-1'>
              {t('pages.confirm.lastNameLabel')}
            </label>
            <input
              type='text'
              id='confirm-last-name'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.confirm.lastNamePlaceholder')}
              value={lastName}
              onChange={(e): void => {
                setLastName(e.target.value);
                setTouched((p) => ({ ...p, lastName: true }));
              }}
            />
            {!validLast && (touched.lastName || isInvalid) && (
              <div className={errorClass}>{t('pages.confirm.lastNameRequired')}</div>
            )}
          </div>
        </div>

        <h3 className='text-base font-medium mt-8 mb-3'>
          {t('pages.confirm.emailPrompt')}
        </h3>

        <div className='mb-3'>
          <label htmlFor='confirm-email' className='block text-sm mb-1'>
            {t('pages.confirm.emailLabel')}
          </label>
          <input
            type='text'
            id='confirm-email'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.confirm.emailPlaceholder')}
            value={email}
            onChange={(e): void => {
              setEmail(e.target.value);
              setTouched((p) => ({ ...p, email: true }));
            }}
          />
          {!validEmail && (touched.email || isInvalid) && (
            <div className={errorClass}>{t('pages.confirm.emailRequired')}</div>
          )}
        </div>

        <div className='mb-3'>
          <label htmlFor='confirm-phone' className='block text-sm mb-1'>
            {t('pages.confirm.phoneLabel')}
          </label>
          <input
            type='tel'
            id='confirm-phone'
            maxLength={12}
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.confirm.phonePlaceholder')}
            value={phone}
            onChange={(e): void => {
              setPhone(formatPhone(e.target.value));
              setTouched((p) => ({ ...p, phone: true }));
            }}
          />
          {!validPhone && (touched.phone || isInvalid) && (
            <div className={errorClass}>{t('pages.confirm.phoneRequired')}</div>
          )}
        </div>

        <div className='text-center mt-8'>
          <Button
            id='continue-button'
            buttonType={ButtonType.primary}
            onClick={submit}
            disabled={submitting}
          >
            {t('pages.confirm.continue')}
          </Button>
          <div className='flex justify-center gap-2 mt-4'>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full inline-block ${
                  i === 0 ? 'bg-sapphire' : 'border border-neutral-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
});
